import type { FishGroupTransferOperationType } from '../historyTypes';
import type {
    FishGroupEventType,
    FishGroupOperationType,
    FishGroupStatus,
    OverviewFishGroupStatus,
} from '../types';

// ---------------------------------------------------------------------------
// Enum guards. Backend ships these as string literal unions; the wire value
// is always one of the known strings, but we defensively fall back to a safe
// default to keep TypeScript narrow and avoid runtime surprises if the
// backend ever adds a value the frontend doesn't yet know about.
// ---------------------------------------------------------------------------

const FISH_GROUP_STATUSES: readonly FishGroupStatus[] = [
    'pending',
    'active',
    'closed',
    'discarded',
    'harvested',
    'merged',
    'split',
];
const OVERVIEW_FISH_GROUP_STATUSES: readonly OverviewFishGroupStatus[] = [
    'active',
    'closed',
    'discarded',
];
const OPERATION_TYPES: readonly FishGroupOperationType[] = [
    'creation',
    'relocation',
    'split',
    'merge',
    'split_and_merge',
    'redistribution',
    'closure',
    'discard',
    'revert',
];
// Must list EVERY member of FishGroupEventType: `asEventType` silently falls
// back to 'measurement' for anything missing here, so an omission does not
// fail loudly — it mislabels the event. Covered by mappers.test.ts.
const EVENT_TYPES: readonly FishGroupEventType[] = [
    'created',
    'closed',
    'relocated',
    'discarded',
    'mortality',
    'harvest',
    'measurement',
    'feeding',
    'treatment',
    'delousing',
    'metadata_updated',
    'reverted',
];

export function asFishGroupStatus(value: string): FishGroupStatus {
    return (FISH_GROUP_STATUSES as readonly string[]).includes(value)
        ? (value as FishGroupStatus)
        : 'active';
}

export function asOverviewFishGroupStatus(value: string): OverviewFishGroupStatus {
    return (OVERVIEW_FISH_GROUP_STATUSES as readonly string[]).includes(value)
        ? (value as OverviewFishGroupStatus)
        : 'active';
}

export function asOperationType(value: string): FishGroupOperationType {
    return (OPERATION_TYPES as readonly string[]).includes(value)
        ? (value as FishGroupOperationType)
        : 'creation';
}

const TRANSFER_OPERATION_TYPES: readonly FishGroupTransferOperationType[] = [
    'split',
    'merge',
    'split_and_merge',
    'redistribution',
];

export function asTransferOperationType(value: string): FishGroupTransferOperationType {
    // Backend guarantees one of the 4 composition-changing types. Default
    // to 'split' on unexpected input rather than throw — the lineage view
    // stays renderable with a sensible fallback color.
    return (TRANSFER_OPERATION_TYPES as readonly string[]).includes(value)
        ? (value as FishGroupTransferOperationType)
        : 'split';
}

export function asEventType(value: string): FishGroupEventType {
    return (EVENT_TYPES as readonly string[]).includes(value)
        ? (value as FishGroupEventType)
        : 'measurement';
}
